import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Button, Modal, Form, Input, Popconfirm, Select } from 'antd';
import { TableOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const TopicTradeTables = () => {
  const [topicTradeTables, setTopicTradeTables] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deletingTable, setDeletingTable] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();
  const [filteredTopics, setFilteredTopics] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadData();
  }, [pagination.current, pagination.pageSize]);

  const loadData = async () => {
    try {
      const [topicTradeTablesRes, topicsRes] = await Promise.all([tradeAPI.getTopicTradeTables(), tradeAPI.getTopics()]);
      const allData = topicTradeTablesRes.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setTopicTradeTables(currentPageData);
      setTopics(topicsRes.data);
      setPagination((prev) => ({ ...prev, total }));
      const filtered = topicsRes.data.filter((topic) => !allData.some((tt) => tt.topic_id === topic.id));
      setFilteredTopics(filtered);
    } catch (error) {
      console.error('加载数据失败:', error);
      setTopicTradeTables([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const fetchTopicTradeTables = async () => {
    try {
      const response = await tradeAPI.getTopicTradeTables();
      setTopicTradeTables(response.data);
      updateFilteredTopics(response.data, topics);
    } catch (error) {
      console.error('获取分表失败:', error);
    }
  };

  const updateFilteredTopics = (tables, allTopics) => {
    const filtered = allTopics.filter((topic) => !tables.some((tt) => tt.topic_id === topic.id));
    setFilteredTopics(filtered);
  };

  const handleCreate = () => {
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingTable(record);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTable) return;

    try {
      await tradeAPI.deleteTopicTradeTable(deletingTable.topic_id);
      message.success('分表删除成功');
      setIsDeleteModalVisible(false);
      setDeletingTable(null);
      fetchTopicTradeTables();
    } catch (error) {
      message.error('删除分表失败');
      console.error('删除分表失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingTable(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      await tradeAPI.createTopicTradeTable(values);
      message.success('分表创建成功');

      setIsModalVisible(false);
      form.resetFields();
      fetchTopicTradeTables();
    } catch (error) {
      message.error(error.response?.data?.detail || '创建分表失败');
      console.error('创建分表失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    return topic ? topic.name : `话题 ${topicId}`;
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'topic_id',
      key: 'topic_id',
      width: 80,
    },
    {
      title: '话题',
      dataIndex: 'topic_id',
      key: 'topic',
      render: (topicId) => getTopicName(topicId),
      width: 200,
    },
    {
      title: '表名',
      dataIndex: 'table_name',
      key: 'table_name',
      width: 200,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
          删除
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>交易话题分表管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建分表
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={topicTradeTables}
        rowKey="topic_id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title="新建分表" open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="topic_id" label="选择话题" rules={[{ required: true, message: '请选择话题' }]}>
            <Select placeholder="请选择话题">
              {filteredTopics.map((topic) => (
                <Option key={topic.id} value={topic.id}>
                  {topic.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除分表 "{deletingTable?.table_name}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该分表及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default TopicTradeTables;
